import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import KLHeader from "../../../components/KLHeader";
import KLPleaseWait from "../../../components/KLPleaseWait";
import KLAvailableSlotSelect from "../../../components/KLAvailableSlotSelect";
import { InfoText, Subtitle, Title } from "../../styles";
import { CardHeadline, CardSubline, Container, CurrentCard } from "./styles";
import { reminderActionsService } from "../../../services/reminderActionsService";
import type { RescheduleContext } from "../../../services/reminderActionsService";
import { AppDispatch } from "../../../store";
import { fetchSlots } from "../../../store/duckers/slots/thunk";
import { UserType } from "../../../constants/types";
import type { ReminderAction } from "../types";
import {
  priorActionVerbHebrew,
  SERVER_ERROR_MSG,
  TOKEN_ERROR_MSG,
} from "../messages";

type ViewState =
  | { kind: "loadingContext" }
  | { kind: "picking"; context: RescheduleContext }
  | { kind: "submitting"; context: RescheduleContext }
  | { kind: "success" }
  | { kind: "alreadyActed"; priorAction: ReminderAction }
  | { kind: "tokenError" }
  | { kind: "notFound" }
  | { kind: "serverError" };

const HEBREW_WEEKDAYS = [
  "ראשון",
  "שני",
  "שלישי",
  "רביעי",
  "חמישי",
  "שישי",
  "שבת",
] as const;

const formatHebrewDayTime = (iso: string): string => {
  const dt = DateTime.fromISO(iso, { zone: "Asia/Jerusalem" });
  if (!dt.isValid) return iso;
  const idx = dt.weekday === 7 ? 0 : dt.weekday;
  return `יום ${HEBREW_WEEKDAYS[idx]} ${dt.toFormat("HH:mm")}`;
};

const errorToState = (err: any): ViewState => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 401) return { kind: "tokenError" };
  if (status === 404) return { kind: "notFound" };
  if (status === 409 && data?.prior_action) {
    return {
      kind: "alreadyActed",
      priorAction: data.prior_action as ReminderAction,
    };
  }
  return { kind: "serverError" };
};

const RescheduleScreen: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [state, setState] = useState<ViewState>({ kind: "loadingContext" });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ kind: "tokenError" });
      return;
    }
    (async () => {
      try {
        const context = await reminderActionsService.getRescheduleContext(token);
        if (!cancelled) setState({ kind: "picking", context });
      } catch (err: any) {
        if (!cancelled) setState(errorToState(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const context = state.kind === "picking" || state.kind === "submitting"
    ? state.context
    : null;

  const duration = useMemo((): number => {
    if (!context) return 0;
    const startDT = DateTime.fromISO(context.current_start_time, {
      zone: "Asia/Jerusalem",
    });
    const endDT = DateTime.fromISO(context.current_end_time, {
      zone: "Asia/Jerusalem",
    });
    if (!startDT.isValid || !endDT.isValid) return 0;
    return endDT.diff(startDT, "minutes").minutes;
  }, [context]);

  useEffect(() => {
    if (context && duration) {
      dispatch(
        fetchSlots({
          businessId: context.business_id,
          duration,
          userType: UserType.Customer,
        }),
      );
    }
  }, [context, duration, dispatch]);

  const handleSelect = async (selectedTime: {
    month: string;
    day: string;
    dayLabel: string;
    time: string;
    displayTime: string;
  }) => {
    if (!token || !context) return;
    const [newStart, newEnd] = selectedTime.time.split(" - ");
    setState({ kind: "submitting", context });
    try {
      await reminderActionsService.reschedule(token, newStart, newEnd);
      setState({ kind: "success" });
    } catch (err: any) {
      setState(errorToState(err));
    }
  };

  return (
    <Container>
      <KLHeader />
      {state.kind === "loadingContext" && (
        <KLPleaseWait text="טוענים את פרטי התור" />
      )}
      {(state.kind === "picking" || state.kind === "submitting") && (
        <>
          <CurrentCard>
            <CardHeadline>
              {`התור הנוכחי: ${formatHebrewDayTime(state.context.current_start_time)}`}
            </CardHeadline>
            {state.context.service_description && (
              <CardSubline>{state.context.service_description}</CardSubline>
            )}
          </CurrentCard>
          {state.kind === "submitting" ? (
            <KLPleaseWait text="מעדכנים את התור" />
          ) : (
            <KLAvailableSlotSelect
              onSelect={handleSelect}
              title="בחירת מועד חדש"
              subtitle="באיזה חודש נעביר?"
              confirmationButtonTitle="הזז לכאן"
            />
          )}
        </>
      )}
      {state.kind === "success" && (
        <>
          <Title>התור הועבר בהצלחה למועד החדש!</Title>
          <Subtitle>שלחנו לכם הודעת סמס עם הפרטים.</Subtitle>
        </>
      )}
      {state.kind === "alreadyActed" && (
        <Title>{`כבר ${priorActionVerbHebrew(state.priorAction)} את התור הזה.`}</Title>
      )}
      {state.kind === "tokenError" && (
        <>
          <Title>{TOKEN_ERROR_MSG}</Title>
          <InfoText>אם קיבלתם את הקישור בטעות, אפשר להתעלם.</InfoText>
        </>
      )}
      {state.kind === "notFound" && (
        <>
          <Title>לא מצאנו את התור. אנא צרו קשר עם העסק.</Title>
        </>
      )}
      {state.kind === "serverError" && <Title>{SERVER_ERROR_MSG}</Title>}
    </Container>
  );
};

export default RescheduleScreen;
